/* global axios */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import Select from 'react-select';
import debounce from 'lodash.debounce';
import api from '../services/apiService';

const STATUS_OPTIONS = [
  { value: 'pending', label: 'pending' },
  { value: 'confirmed', label: 'confirmed' },
  { value: 'active', label: 'active' },
  { value: 'completed', label: 'completed' },
];

function OrdersEntryReport() {
  // Filter
  const [search, setSearch] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');
  
  const [statusOptions, setStatusOptions] = useState(STATUS_OPTIONS);
  const [statusSelection, setStatusSelection] = useState(STATUS_OPTIONS); // default: alle 4

  // Paging + Daten
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [sumNet, setSumNet] = useState(0);
  const [sumGross, setSumGross] = useState(0);

  // UI
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  // Kundensuche
  const [customerOptions, setCustomerOptions] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // expand + lazy items
  const [expanded, setExpanded] = useState({});
  const [itemsByOrderId, setItemsByOrderId] = useState({});
  const [rowItemLoading, setRowItemLoading] = useState({});

  const toggleRow = async (order) => {
    const id = order.id;
    const willOpen = !expanded[id];
    setExpanded(prev => ({ ...prev, [id]: willOpen }));

    if (!willOpen) return;
    if ((Array.isArray(order.orderItems) && order.orderItems.length) ||
        (Array.isArray(itemsByOrderId[id]) && itemsByOrderId[id].length)) {
      return;
    }
    

    try {
      setRowItemLoading(prev => ({ ...prev, [id]: true }));
      const { data } = await api.get(`/orders/${id}/orderitems`);
      setItemsByOrderId(prev => ({ ...prev, [id]: Array.isArray(data) ? data : [] }));
    } catch (e) {
      console.error(`OrderItems für Auftrag ${id} konnten nicht geladen werden:`, e);
      setItemsByOrderId(prev => ({ ...prev, [id]: [] }));
    } finally {
      setRowItemLoading(prev => ({ ...prev, [id]: false }));
    }
  };

  // helpers
  const buildParams = useCallback(() => {
    const params = { page, pageSize };
    if (search) params.search = search;
    if (customerId) params.customerId = customerId;
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    if (createdFrom) params.createdFrom = createdFrom;
    if (createdTo) params.createdTo = createdTo;
    
    const selectedStatuses = (statusSelection || []).map(s => s.value).filter(Boolean);
    if (selectedStatuses.length) params.status = selectedStatuses.join(',');
    return params;
  }, [page, pageSize, search, customerId, dateFrom, dateTo, createdFrom, createdTo, statusSelection]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setErrMsg('');
    try {
      const params = buildParams();
      const { data } = await api.get('/reports/orders/entry', { params });
      setRows(data.data || []);
      setTotal(Number(data.total ?? 0));
      setPages(Math.max(1, Number(data.pages ?? 1)));
      setSumNet(Number(data.sumNet || 0));
      setSumGross(Number(data.sumGross || 0));
    } catch (e) {
      console.error('OrdersEntryReport laden fehlgeschlagen:', e);
      setErrMsg('Fehler beim Laden des Berichts');
      setRows([]); setTotal(0); setPages(0); setSumNet(0); setSumGross(0);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  // Kundenliste (debounced)
  const loadCustomers = useMemo(() => debounce(async (q) => {
    try {
      const { data } = await api.get('/customers', { params: { search: q || '', limit: 10 } });
      const opts = (Array.isArray(data) ? data : []).map(c => ({
        value: c.id,
        label: `${c.company} (${c.customerNumber || '—'})`,
        isBlocked: !!c.isBlocked,
        blockedUntil: c.blockedUntil || null,
        blockReason: c.blockReason || null,
      }));
      setCustomerOptions(opts);
    } catch (e) {
      console.error('Kunden laden fehlgeschlagen:', e);
      setCustomerOptions([]);
    }
  }, 300), []);

  useEffect(() => {
    loadCustomers('');
    return () => loadCustomers.cancel();
  }, [loadCustomers]);

  const handleCustomerInputChange = (inputValue, { action }) => {
    if (action === 'input-change') loadCustomers(inputValue);
  };

  const handleCustomerChange = (opt) => {
    setSelectedCustomer(opt || null);
    setCustomerId(opt ? String(opt.value) : '');
    setPage(1);
  };

  const customerOptionStyles = {
    option: (base, { data, isFocused, isSelected }) => ({
      ...base,
      color: data.isBlocked ? '#b91c1c' : base.color,
      fontWeight: data.isBlocked ? 600 : 400,
      backgroundColor: isFocused
        ? (data.isBlocked ? '#fee2e2' : base.backgroundColor)
        : isSelected ? base.backgroundColor : 'transparent',
    }),
    singleValue: (base, { data }) => ({
      ...base,
      color: data?.isBlocked ? '#b91c1c' : base.color,
      fontWeight: data?.isBlocked ? 600 : 400,
    }),
  };

  const customerFormatOptionLabel = (data) => (
    <span
      title={
        data.isBlocked
          ? (data.blockReason
              ? `Gesperrt${data.blockedUntil ? ` bis ${new Date(data.blockedUntil).toLocaleDateString('de-DE')}` : ''} – Grund: ${data.blockReason}`
              : `Gesperrt${data.blockedUntil ? ` bis ${new Date(data.blockedUntil).toLocaleDateString('de-DE')}` : ''}`
            )
          : ''
      }
    >
      {data.isBlocked ? '🚫 ' : ''}{data.label}
    </span>
  );

  // Initial + Paging
  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchData(); }, [page, pageSize, fetchData]);

  // Auto-Reload bei Filteränderungen
// Auto-Reset der Seite bei Filteränderungen (ohne fetchData-Aufruf)
useEffect(() => {
  setPage(1);
}, [customerId, search, dateFrom, dateTo, createdFrom, createdTo, statusSelection]);




  const handleSubmit = async (e) => {
    e.preventDefault();
    if (page !== 1) setPage(1);
    else await fetchData();
  };

  // CSV
  const handleDownloadCSV = async () => {
    try {
      const params = { ...buildParams(), format: 'csv' };
      const response = await api.get('/reports/orders/entry', { params, responseType: 'blob' });
      const cd = response.headers['content-disposition'] || '';
      const m = cd.match(/filename="?([^"]+)"?/i);
      const filename = m?.[1] || 'orders_entry_report.csv';
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('CSV-Download fehlgeschlagen:', e);
      setErrMsg('CSV-Download fehlgeschlagen');
    }
  };

  const gotoPrev = () => setPage(p => Math.max(1, p - 1));
  const gotoNext = () => setPage(p => Math.min(pages || 1, p + 1));

  return (
    <div style={{ maxWidth: 1100, margin: 'auto' }}>
      <Helmet>
        <title>Bericht: Auftrags Eingang</title>
        <meta name="Orders Entry Report" content="Report Orders Entry" />
      </Helmet>

      <h2>📊 Auftrag Eingang</h2>

      <form onSubmit={handleSubmit} style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
          {/* Kunde */}
          <div style={{ flex: '1 1 260px', minWidth: 260 }}>
            <label htmlFor="customerSelect" style={{ display: 'inline-block', marginBottom: 4 }}>
              Kunde
            </label>
            <Select
              inputId="customerSelect"
              className="select-box-cst"
              isClearable
              placeholder="Kunde suchen…"
              options={customerOptions}
              value={selectedCustomer}
              onInputChange={handleCustomerInputChange}
              onChange={handleCustomerChange}
              getOptionValue={(o) => String(o.value)}
              getOptionLabel={(o) => (o.isBlocked ? `🚫 ${o.label}` : o.label)}
              formatOptionLabel={customerFormatOptionLabel}
              styles={{
                ...customerOptionStyles,
                container: (base) => ({ ...base, marginTop: -5 }),
                control:   (base) => ({ ...base, minHeight: 34, width: '100%', marginBottom: 16 }),
              }}
            />
          </div>

          {/* Auftragsdatum ab/bis */}
          <div style={{ flex: '1 1 160px', minWidth: 160 }}>
            <label>Auftragsdatum ab</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ width: '100%' }} />
          </div>
          <div style={{ flex: '1 1 160px', minWidth: 160 }}>
            <label>Auftragsdatum bis</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ width: '100%' }} />
          </div>

          {/* Erstellt ab/bis */}
          <div style={{ flex: '1 1 160px', minWidth: 160 }}>
            <label>Erstellt ab</label>
            <input type="date" value={createdFrom} onChange={e => setCreatedFrom(e.target.value)} style={{ width: '100%' }} />
          </div>
          <div style={{ flex: '1 1 160px', minWidth: 160 }}>
            <label>Erstellt bis</label>
            <input type="date" value={createdTo} onChange={e => setCreatedTo(e.target.value)} style={{ width: '100%' }} />
          </div>

          {/* Status (Multi) */}
          <div style={{ flex: '1 1 240px', minWidth: 240 }}>
            <label>Status</label>
            <Select
              isMulti
              options={statusOptions}
              value={statusSelection}
              onChange={setStatusSelection}
              styles={{ control: (b) => ({ ...b, minHeight: 34, width: '100%', marginBottom: 16 }) }}
              placeholder="Status wählen…"
            />
          </div>

          {/* Suche */}
          <div style={{ flex: '1 1 200px', minWidth: 200 }}>
            <label>Suche</label>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Nr., Kunde, Besteller…"
              style={{ width: '100%' }}
            />
          </div>

          {/* Rechts: Checkbox + CSV */}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12, flex: '0 0 auto' }}>
  <button type="button" onClick={handleDownloadCSV} disabled={loading}>CSV herunterladen</button>
</div>

        </div>
      </form>

      {errMsg && <p style={{ color: 'red' }}>{errMsg}</p>}

      <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between' }}>
        <div>
          <strong>Gesamt:</strong> {total} Einträge &nbsp;|&nbsp;
          <strong> Summe Netto:</strong> {sumNet.toLocaleString('de-DE', { minimumFractionDigits: 2 })} € &nbsp;|&nbsp;
          <strong> Summe Brutto:</strong> {sumGross.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
        </div>
        <div>
          <button onClick={gotoPrev} disabled={loading || page <= 1}>Zurück</button>
          <span style={{ margin: '0 8px' }}>Seite {page} / {pages || 1}</span>
          <button onClick={gotoNext} disabled={loading || page >= (pages || 1)}>Weiter</button>
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#fffefeff' }}>
              <th style={{ width: 34 }}></th>
              <th style={{ textAlign: 'left', padding: 8 }}>Nr.</th>
              <th style={{ textAlign: 'left', padding: 8 }}>Kunde</th>
              <th style={{ textAlign: 'left', padding: 8 }}>Besteller</th>
              <th style={{ textAlign: 'left', padding: 8 }}>Status</th>
              <th style={{ textAlign: 'left', padding: 8 }}>Datum</th>
              <th style={{ textAlign: 'right', padding: 8 }}>Netto (€)</th>
              <th style={{ textAlign: 'right', padding: 8 }}>Brutto (€)</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr><td colSpan="8" style={{ padding: 12, textAlign: 'center' }}>Keine Daten</td></tr>
            )}

            {rows.map(r => {
              const responseItems = Array.isArray(r.orderItems) ? r.orderItems : null;
              const cachedItems = itemsByOrderId[r.id];
              const visibleItems = responseItems && responseItems.length ? responseItems : (cachedItems || []);
              const isOpen = !!expanded[r.id];

              return (
                <React.Fragment key={r.id}>
                  <tr style={{ borderTop: '1px solid #ddd' }}>
                    <td style={{ padding: 0, textAlign: 'center' }}>
  <button
    type="button"
    onClick={() => toggleRow(r)}
    title={isOpen ? 'Positionen verbergen' : 'Positionen anzeigen'}
    aria-label={isOpen ? 'Positionen verbergen' : 'Positionen anzeigen'}
    style={{
      border: 'none',
      background: 'transparent',
      cursor: 'pointer',
      padding: 4,
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      fontSize: 16,
    }}
  >
    <span style={{ fontSize: 16 }}>📦</span>
  </button>
</td>


                    <td style={{ padding: 8 }}>{r.orderNumber || '—'}</td>
                    <td style={{ padding: 8 }}>{r.customer?.company || '—'}</td>
                    <td style={{ padding: 8 }}>{r.contact?.name || '—'}</td>
                    <td style={{ padding: 8 }}>{r.status || '—'}</td>
                    <td style={{ padding: 8 }}>{r.date ? new Date(r.date).toLocaleDateString('de-DE') : '—'}</td>
                    <td style={{ padding: 8, textAlign: 'right' }}>
                      {Number(r.sumNet || 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })}
                    </td>
                    <td style={{ padding: 8, textAlign: 'right' }}>
                      {Number(r.sumGross || 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>

                 {isOpen && (
                    <tr>
                      <td colSpan={8} style={{ background: '#ffffffff', padding: 8 }}>
                        {rowItemLoading[r.id] ? (
                          <div style={{ padding: 6 }}>Positionen werden geladen…</div>
                        ) : visibleItems.length === 0 ? (
                          <div style={{ padding: 6 }}>Keine Positionen vorhanden.</div>
                        ) : (
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr>
                                <th style={{ textAlign: 'left', padding: 6 }}>Pos.-ID</th>
                                <th style={{ textAlign: 'left', padding: 6 }}>Artikel</th>
                                <th style={{ textAlign: 'right', padding: 6 }}>Menge</th>
                                <th style={{ textAlign: 'right', padding: 6 }}>Einheit</th>
                                <th style={{ textAlign: 'right', padding: 6 }}>E-Preis</th>
                                <th style={{ textAlign: 'right', padding: 6 }}>Gesamt</th>
                                <th style={{ textAlign: 'left', padding: 6 }}>Von</th>
                                <th style={{ textAlign: 'left', padding: 6 }}>Bis</th>
                              </tr>
                            </thead>
                            <tbody>
                              {visibleItems.map(oi => (
                                <tr key={oi.id} style={{ borderTop: '1px solid #ffffffff' }}>
                                  <td style={{ padding: 6 }}>{oi.id}</td>
                                  <td style={{ padding: 6 }}>{oi.item?.name || `#${oi.itemId}`}</td>
                                  <td style={{ padding: 6, textAlign: 'right' }}>{oi.quantity}</td>
                                  <td style={{ padding: 6, textAlign: 'right' }}>
                                    {oi.unit?.name || oi.unit?.shortName || oi.unitId || '—'}
                                  </td>
                                  <td style={{ padding: 6, textAlign: 'right' }}>
                                    {Number(oi.pricePerUnit || 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
                                  </td>
                                  <td style={{ padding: 6, textAlign: 'right' }}>
                                    {Number(oi.total || 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
                                  </td>
                                  <td style={{ padding: 6 }}>
                                    {oi.rentalPeriodFrom ? new Date(oi.rentalPeriodFrom).toLocaleDateString('de-DE') : '—'}
                                  </td>
                                  <td style={{ padding: 6 }}>
                                    {oi.rentalPeriodTo ? new Date(oi.rentalPeriodTo).toLocaleDateString('de-DE') : '—'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {loading && <p style={{ marginTop: 8 }}>Lade…</p>}
    </div>
  );
}

export default OrdersEntryReport;
